/**
 * Copyright 2025 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: MIT-0
 */

import {
  Box, Typography, Grid, Card, CardContent, LinearProgress,
  Chip, List, ListItem, ListItemIcon, ListItemText, Divider,
  Button, Alert, CircularProgress,
} from '@mui/material';
import {
  CheckCircle, Warning, Error as ErrorIcon,
  Speed, Storage, NetworkCheck, Refresh, TrendingUp,
} from '@mui/icons-material';
import { useState, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { GET_SYSTEM_HEALTH } from '../graphql/queries';

// ── Types ─────────────────────────────────────────────────────────────────────

interface QueueHealth {
  name: string;
  depth: number;
  inFlight: number;
  isDLQ: boolean;
  status: 'healthy' | 'warning' | 'critical' | 'unknown';
}

interface ApiErrorRate {
  apiName: string;
  errorCount4xx: number;
  errorCount5xx: number;
  windowMinutes: number;
  status: 'healthy' | 'degraded' | 'outage' | 'unknown';
}

interface ErrorLogVolume {
  todayCount: number;
  yesterdayCount: number;
  trend: 'normal' | 'elevated' | 'spike' | 'unknown';
}

interface OperationalCounts {
  activeDrivers: number;
  activeTrips: number;
  pendingRequests: number;
}

interface SystemHealth {
  queues: QueueHealth[];
  apiErrors: ApiErrorRate[];
  errorLogVolume: ErrorLogVolume;
  operationalCounts: OperationalCounts;
  generatedAt: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const QUEUE_LABELS: Record<string, { label: string; description: string }> = {
  rideMatchingQueue:       { label: 'Ride Matching',          description: 'Incoming ride requests waiting to be matched with a driver' },
  rideMatchingDLQ:         { label: 'Ride Matching — Failed', description: 'Ride requests that could not be matched after all retries' },
  driverTimeoutQueue:      { label: 'Driver Timeout',         description: 'Drivers who accepted a ride but have not responded in time' },
  driverTimeoutDLQ:        { label: 'Driver Timeout — Failed',description: 'Timeout events that failed to process' },
  earningsProcessingQueue: { label: 'Earnings Processing',    description: 'Completed trips waiting for driver earnings to be calculated' },
  earningsProcessingDLQ:   { label: 'Earnings — Failed',      description: 'Earnings calculations that failed and need investigation' },
  cashoutProcessingQueue:  { label: 'Cashout Processing',     description: 'Driver cashout requests waiting to be paid out' },
  cashoutProcessingDLQ:    { label: 'Cashout — Failed',       description: 'Cashout requests that failed to process' },
};

const queueLabel = (name: string) =>
  QUEUE_LABELS[name]?.label ?? name;

const queueDescription = (name: string) =>
  QUEUE_LABELS[name]?.description ?? null;

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'healthy' || status === 'operational') return 'success';
  if (status === 'warning' || status === 'degraded' || status === 'elevated') return 'warning';
  if (status === 'critical' || status === 'outage' || status === 'spike') return 'error';
  return 'default';
};

const StatusIcon = ({ status }: { status: string }) => {
  if (status === 'healthy' || status === 'operational') return <CheckCircle color="success" />;
  if (status === 'warning' || status === 'degraded' || status === 'elevated') return <Warning color="warning" />;
  if (status === 'critical' || status === 'outage' || status === 'spike') return <ErrorIcon color="error" />;
  return <CheckCircle color="disabled" />;
};

const trendLabel = (trend: string) => {
  if (trend === 'normal') return 'Normal';
  if (trend === 'elevated') return 'Elevated (>2× yesterday)';
  if (trend === 'spike') return 'Spike (>5× yesterday)';
  return 'Unknown';
};

// ── Component ─────────────────────────────────────────────────────────────────

function SystemStatus() {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const client = generateClient();
      const result: any = await client.graphql({ query: GET_SYSTEM_HEALTH });
      const data: SystemHealth = result.data.getSystemHealth;
      setHealth(data);
      setLastUpdated(new Date());
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load system health');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + 60-second auto-refresh
  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 60_000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  // Derive overall status from queues + API errors
  const overallStatus = (() => {
    if (!health) return 'unknown';
    const hasCritical = health.queues.some(q => q.status === 'critical')
      || health.apiErrors.some(a => a.status === 'outage');
    const hasWarning = health.queues.some(q => q.status === 'warning')
      || health.apiErrors.some(a => a.status === 'degraded')
      || health.errorLogVolume.trend === 'spike';
    if (hasCritical) return 'outage';
    if (hasWarning) return 'degraded';
    return 'operational';
  })();

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>System Status</Typography>
          <Typography variant="body2" color="textSecondary">
            {lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()} · auto-refreshes every 60s` : 'Loading…'}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
          onClick={fetchHealth} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* Overall status */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <StatusIcon status={overallStatus} />
            <Box>
              <Typography variant="h6">
                System Status: {overallStatus.charAt(0).toUpperCase() + overallStatus.slice(1)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {overallStatus === 'operational' ? 'All systems are operational'
                  : overallStatus === 'degraded' ? 'Some services are experiencing issues'
                  : overallStatus === 'outage' ? 'Major service disruption detected'
                  : 'Loading health data…'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Operational counts */}
      {health && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { label: 'Active Drivers', value: health.operationalCounts.activeDrivers, icon: <Speed /> },
            { label: 'Active Trips', value: health.operationalCounts.activeTrips, icon: <NetworkCheck /> },
            { label: 'Pending Requests', value: health.operationalCounts.pendingRequests, icon: <Storage /> },
            {
              label: 'Error Log Trend',
              value: trendLabel(health.errorLogVolume.trend),
              sub: `Today: ${health.errorLogVolume.todayCount} · Yesterday: ${health.errorLogVolume.yesterdayCount}`,
              icon: <TrendingUp />,
              chipStatus: health.errorLogVolume.trend === 'normal' ? 'healthy' : health.errorLogVolume.trend,
            },
          ].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.label}>
              <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {item.icon}
                    <Typography variant="subtitle2" color="textSecondary">{item.label}</Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 0.5 }}>{item.value}</Typography>
                  {item.sub && (
                    <Typography variant="caption" color="textSecondary">{item.sub}</Typography>
                  )}
                  {item.chipStatus && (
                    <Box mt={1}>
                      <Chip label={item.chipStatus} color={statusColor(item.chipStatus)} size="small" />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* API error rates */}
      {health && health.apiErrors.length > 0 && (
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>API Health (last 5 min)</Typography>
            <List disablePadding>
              {health.apiErrors.map((api, i) => (
                <Box key={api.apiName}>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <StatusIcon status={api.status} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2">{api.apiName}</Typography>
                          <Chip label={api.status} color={statusColor(api.status)} size="small" />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          4xx: {api.errorCount4xx} · 5xx: {api.errorCount5xx} · window: {api.windowMinutes}m
                        </Typography>
                      }
                    />
                  </ListItem>
                  {i < health.apiErrors.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Queue health */}
      {health && (
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Queue Health</Typography>
            <List disablePadding>
              {health.queues.map((q, i) => (
                <Box key={q.name}>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <StatusIcon status={q.status} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2">{queueLabel(q.name)}</Typography>
                          {q.isDLQ && <Chip label="Dead Letter" size="small" variant="outlined" color="error" />}
                          <Chip label={q.status} color={statusColor(q.status)} size="small" />
                        </Box>
                      }
                      secondary={
                        <Box>
                          {queueDescription(q.name) && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {queueDescription(q.name)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="textSecondary">
                            {q.depth >= 0
                              ? `Waiting: ${q.depth} · In-flight: ${q.inFlight}`
                              : 'Unavailable'}
                          </Typography>
                        </Box>
                      }
                    />
                    {q.depth > 0 && (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(q.depth / 20 * 100, 100)}
                        color={statusColor(q.status) as any}
                        sx={{ width: 80, ml: 2 }}
                      />
                    )}
                  </ListItem>
                  {i < health.queues.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Alerts summary */}
      {health && overallStatus !== 'operational' && (
        <Box mt={3}>
          <Alert severity={overallStatus === 'outage' ? 'error' : 'warning'}>
            <Typography variant="subtitle2" gutterBottom>Active Alerts</Typography>
            {health.queues.filter(q => q.status !== 'healthy' && q.status !== 'unknown').map(q => (
              <Typography key={q.name} variant="body2">
                • {queueLabel(q.name)}: {q.status} ({q.depth} messages waiting)
              </Typography>
            ))}
            {health.apiErrors.filter(a => a.status !== 'healthy' && a.status !== 'unknown').map(a => (
              <Typography key={a.apiName} variant="body2">
                • {a.apiName}: {a.status} ({a.errorCount5xx} 5xx errors in last 5 min)
              </Typography>
            ))}
            {health.errorLogVolume.trend !== 'normal' && health.errorLogVolume.trend !== 'unknown' && (
              <Typography variant="body2">
                • Error log volume {health.errorLogVolume.trend}: {health.errorLogVolume.todayCount} today vs {health.errorLogVolume.yesterdayCount} yesterday
              </Typography>
            )}
          </Alert>
        </Box>
      )}
    </Box>
  );
}

export default SystemStatus;
